import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useToasts } from 'react-toast-notifications';
import IntlDate from 'src/components/intl-date';
import Loader from 'src/components/loader';
import { api } from 'src/utils/api';
import { API_RESOURCES, RUN_OPERATIONS } from 'src/utils/constants';
import { FORMATTED_DURATION_TYPES, FormattedOptionalDuration } from 'src/utils/dateUtils';

import CollapsableCard from '../collapsable-card';

const PrintDetails = ({ pieceURI }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [printingRun, setPrintingRun] = useState(null);
  const [runActuals, setRunActuals] = useState(null);
  const { addToast } = useToasts();

  const actuals = useMemo(() => {
    const start = runActuals?.start_in_progress_time;
    const end = runActuals?.end_in_progress_time;

    const startTime = start ? dayjs(start) : null;
    const endTime = end ? dayjs(end) : null;

    const timeInSeconds = (startTime && endTime) ? endTime.diff(startTime, 'seconds') : null;

    return { start, end, time: timeInSeconds };
  }, [runActuals]);

  const onInitialize = async () => {
    // If there's no URI, reset state and do nothing.
    if (!pieceURI) {
      setPrintingRun(null);
      setRunActuals(null);
      return;
    }

    setIsLoading(true);
    try {
      // 1. Fetch prints related to the piece
      const printsResponse = await api.get(`${API_RESOURCES.PRINT}/`, {
        searchParams: { 'filter[piece]': pieceURI },
      }).json();

      const prints = printsResponse?.resources;
      const printingRunURIs = prints?.map(print => print.run).filter(Boolean);

      if (!printingRunURIs || printingRunURIs.length === 0) {
        setPrintingRun(null); // No associated runs found
        return;
      }

      // 2. Fetch the runs
      const runsResponse = await api.get(`${API_RESOURCES.RUN}/`, {
        searchParams: { 'filter[uri]': printingRunURIs.join(',') },
      }).json();

      const runs = runsResponse?.resources;

      const foundPrintingRun = runs?.find(run => run.operation === RUN_OPERATIONS.PRINTING);
      setPrintingRun(foundPrintingRun);

      // 3. If a run was found, fetch its actuals immediately.
      if (foundPrintingRun) {
        const runActualsResponse = await api.get(`${API_RESOURCES.RUN_ACTUALS}/`, {
          searchParams: { 'filter[run]': foundPrintingRun.uri },
        }).json();
        setRunActuals(runActualsResponse?.resources?.[0] ?? null);
      } else {
        setRunActuals(null); // Cleared actuals if no run is found
      }
    } catch (error) {
      addToast('There was an error fetching print details. Please try again.', { appearance: 'error' });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void onInitialize();
  }, [pieceURI]);

  if (isLoading) {
    return <Loader />;
  }

  if (!printingRun) {
    return null;
  }

  return (
    <CollapsableCard title="Primary Run Overview:" subTitle={printingRun.name ?? ''}>
      <Table striped bordered className="mb-0 align-left">
        <tbody>
          <tr>
            <th>Run ID</th>
            <td>{printingRun.customer_id ?? 'N/A'}</td>
          </tr>
          <tr>
            <th>Number of Pieces</th>
            <td>{printingRun.prints.length ?? 'N/A'}</td>
          </tr>
          <tr>
            <th>Workstation Name</th>
            <td>{printingRun.workstation_name ?? 'N/A'}</td>
          </tr>
          <tr>
            <th colSpan="2" className="text-center">
              Run Actuals
            </th>
          </tr>
          <tr>
            <th>Start Time</th>
            <td>{actuals.start ? (
              <IntlDate withTime date={actuals.start} />
            ) : "N/A"}
            </td>
          </tr>
          <tr>
            <th>End Time</th>
            <td>{actuals.end ? (
              <IntlDate withTime date={actuals.end} />
            ) : "N/A"}
            </td>
          </tr>
          <tr>
            <th>Duration</th>
            <td>{actuals?.time ? (
              <FormattedOptionalDuration
                intervalFormat={FORMATTED_DURATION_TYPES.HH_MM_SS}
                value={actuals.time}
              />
            ) : "N/A"}
            </td>
          </tr>
        </tbody>
      </Table>
    </CollapsableCard>
  );
};

PrintDetails.propTypes = {
  pieceURI: PropTypes.string.isRequired,
};

export default PrintDetails;
